import { GlobalOutlined } from "@ant-design/icons"
import { Button, Select, TimePicker, Typography } from "antd"
import dayjs from "dayjs"
import React, { useEffect, useState } from "react"

import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import {
  type CompanyDefaultsConfiguration,
  getCompanyDefaults,
  updateCompanyDefaults
} from "../../api/company-defaults.services.ts"
import { SettingsCard } from "../common/SettingsCard.tsx"

const { Text } = Typography

const TIMEZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "UTC"
]
const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"]

// Every branch that hasn't customized its own timezone/working days/business
// hours falls back to these — the authoritative company-wide baseline, not
// an arbitrary branch's own settings (see BranchConfigurationTab, which
// shows the same three keys per branch with a "Using Company HQ defaults" /
// "Customized for this branch" source tag).
export function CompanyDefaultsConfigCard() {
  const [config, setConfig] = useState<CompanyDefaultsConfiguration | null>(null)
  const [saving, setSaving] = useState(false)
  const [timezone, setTimezone] = useState<string>()
  const [days, setDays] = useState<string[]>([])
  const [hours, setHours] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null)

  const load = async () => {
    const response = await getCompanyDefaults()
    if (response.ok && response.data) {
      setConfig(response.data)
      setTimezone(response.data.default_timezone)
      setDays(response.data.default_working_days || [])
      const businessHours = response.data.default_business_hours || {}
      setHours([
        dayjs(businessHours.start || "08:00", "HH:mm"),
        dayjs(businessHours.end || "17:00", "HH:mm")
      ])
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const save = async () => {
    setSaving(true)
    try {
      const response = await updateCompanyDefaults({
        default_timezone: timezone,
        default_working_days: days,
        default_business_hours: {
          start: hours?.[0]?.format("HH:mm"),
          end: hours?.[1]?.format("HH:mm")
        }
      })
      showResponseMessage(response, "Company defaults updated successfully.")
      if (response.ok) await load()
    } finally {
      setSaving(false)
    }
  }

  if (!config) return null

  return (
    <SettingsCard
      icon={<GlobalOutlined />}
      iconWrapperClassName="bg-blue-50 text-blue-500"
      title="Company-wide defaults"
      subtitle="Every branch that hasn't customized its own time zone, working days or business hours uses these."
    >
      <div className="flex flex-col gap-3">
        <div>
          <Text className="block mb-1">Time zone</Text>
          <Select
            className="w-64"
            value={timezone}
            onChange={setTimezone}
            options={TIMEZONES.map((tz) => ({ value: tz, label: tz }))}
          />
        </div>
        <div>
          <Text className="block mb-1">Working days</Text>
          <Select
            mode="multiple"
            className="w-96"
            value={days}
            onChange={setDays}
            options={DAYS.map((d) => ({ value: d, label: d }))}
          />
        </div>
        <div>
          <Text className="block mb-1">Business hours</Text>
          <TimePicker.RangePicker
            value={hours}
            format="HH:mm"
            onChange={(v) => setHours(v as [dayjs.Dayjs, dayjs.Dayjs])}
          />
        </div>
        <div>
          <Button type="primary" loading={saving} onClick={save}>
            Save
          </Button>
        </div>
      </div>
    </SettingsCard>
  )
}
